import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getUserFavorites, postUserFavorites } from '@entities/user'
import { useAuthStore } from '@features/auth'

const FAVORITES_QUERY_KEY = ['user', 'favorites']
const pendingFavoriteSymbols = new Set<string>()

export const useFavorites = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient()
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)

  const query = useQuery({
    queryKey: FAVORITES_QUERY_KEY,
    queryFn: async () => {
      const data = await getUserFavorites()
      return data.favorites ?? []
    },
    enabled: isLoggedIn && enabled,
  })

  const mutation = useMutation({
    mutationFn: async ({ symbol, isAdd }: { symbol: string; isAdd: boolean }) => {
      return postUserFavorites({ Symbol: symbol, isAdd })
    },
    onMutate: async ({ symbol, isAdd }) => {
      await queryClient.cancelQueries({ queryKey: FAVORITES_QUERY_KEY })

      const previous = queryClient.getQueryData<FavoriteItem[]>(FAVORITES_QUERY_KEY)

      queryClient.setQueryData<FavoriteItem[]>(FAVORITES_QUERY_KEY, (old = []) => {
        if (isAdd) {
          if (old.some((f) => f.symbol === symbol)) return old
          return [...old, { accID: '', symbol, isDefault: 0 }]
        }
        return old.filter((f) => f.symbol !== symbol)
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(FAVORITES_QUERY_KEY, context.previous)
      }
    },
    onSettled: (_data, _error, variables) => {
      pendingFavoriteSymbols.delete(variables.symbol)
      queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY })
    },
  })

  // 매 렌더 새 Set 생성 방지 — 행 컴포넌트(MarketRow) memo 실효화에 필요
  const favoriteSymbols = useMemo(
    () => new Set((query.data ?? []).map((f) => f.symbol)),
    [query.data],
  )

  const { mutate } = mutation
  const toggleFavoriteFn = useCallback(
    (symbol: string, isAdd: boolean) => {
      if (pendingFavoriteSymbols.has(symbol)) return
      pendingFavoriteSymbols.add(symbol)
      mutate({ symbol, isAdd })
    },
    [mutate],
  )

  return {
    ...query,
    favoriteSymbols,
    toggleFavorite: toggleFavoriteFn,
  }
}
